import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { releaseNotesApi } from '../../../../api/release-notes';
import { Button } from '../../../../components/ui/atoms/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../../components/ui/molecules/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../../components/ui/molecules/table';
import { errorMessage, formatDateTime } from '../utils';

const ALL = '__all__';

export function HistoryTab() {
  const [page, setPage] = useState(1);
  const [version, setVersion] = useState('');

  const notes = useQuery({
    queryKey: ['release-notes'],
    queryFn: ({ signal }) => releaseNotesApi.list(signal).then((r) => r.data),
  });
  const sends = useQuery({
    queryKey: ['release-note-sends', page, version],
    queryFn: ({ signal }) => releaseNotesApi.sends({ page, limit: 20, version: version || undefined }, signal).then((r) => r.data),
  });

  const rows = sends.data?.data ?? [];
  const meta = sends.data?.meta;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground" htmlFor="history-version">Version</label>
        <Select value={version || ALL} onValueChange={(v) => { setVersion(v === ALL ? '' : v); setPage(1); }}>
          <SelectTrigger id="history-version" className="w-48">
            <SelectValue placeholder="All versions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All versions</SelectItem>
            {(notes.data ?? []).map((n) => <SelectItem key={n.version} value={n.version}>{`v${n.version}`}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {sends.isError ? (
        <p className="text-sm text-destructive">{errorMessage(sends.error, 'Could not load the history.')}</p>
      ) : sends.isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Version</TableHead>
                <TableHead>Sent to</TableHead>
                <TableHead>Sent by</TableHead>
                <TableHead>Sent at</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">Nothing has been sent yet.</TableCell>
                </TableRow>
              )}
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-medium">{`v${row.version}`}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 font-medium">
                      {row.recipient.name}
                      {row.recipient.isGroup && <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">Group</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">{row.recipient.email}</div>
                  </TableCell>
                  <TableCell>{row.sentBy.name}</TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">{formatDateTime(row.sentAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {meta && meta.totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <span className="text-sm text-muted-foreground">{`Page ${meta.page} of ${meta.totalPages}`}</span>
              <Button variant="outline" size="sm" disabled={page >= meta.totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
